import { createAsyncThunk } from '@reduxjs/toolkit';

import {
  CareerPathResponse,
  CareerCategory,
  CareerPath,
  Industry,
  CareerPathCompensation,
  CareerTrack,
  CareerPathSampleJobs,
  CareerPathAIDisruption,
} from '@backrs_ui/common-ui';

import {
  CareerPathRole,
  CareerPathMeaning,
  CareerPathEnjoyment,
} from '@backrs_ui/grow-ui/src/models/models';
import { fetchWrapper as fetch } from '@backrs_ui/grow-ui/src/utils/fetch/fetchWrapper';

const ENVIRONMENT_URL = process.env.REACT_APP_API_BASEURL;

/** What we expect from the BE */
export type CareerPathContentResponse = Readonly<{
  careerCategories: CareerCategory[];
  careerPaths: CareerPathResponse[];
  careerPathRoles: CareerPathRole[];
  careerPathMeanings: CareerPathMeaning[];
  careerPathEnjoyments: CareerPathEnjoyment[];
  industries: Industry[];
  careerPathCompensations: CareerPathCompensation[];
  careerPathSampleJobs: CareerPathSampleJobs[];
  careerTracks: CareerTrack[];
  careerPathAIDisruptions: CareerPathAIDisruption[];
}>;

export const fetchCareerPathsAndRoles = createAsyncThunk(
  'careerPath/fetchCarerPaths',
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetch(`${ENVIRONMENT_URL}/career-path`);

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();

      return data as CareerPathContentResponse;
    } catch (error) {
      return rejectWithValue(`Failed to fetch career questions with error: ${error}`);
    }
  },
);

export function mapCareerPathResponseToCareerPath(response: CareerPathResponse[]): CareerPath[] {
  return response.map((path) => ({
    id: path.id,
    career_category_id: path.career_category_id,
    name: path.name,
    description: path.description,
    insight: path.insight,
    early_career_hires_per_year: {
      skilled_technical: path.early_career_hires_per_year_skilled_technical,
      professional: path.early_career_hires_per_year_professional,
    },
    growth: path.growth,
    ai_disruption: path.ai_disruption,
    career_track_distribution: {
      SEMISKILLED: path.career_track_distribution_semi_skilled,
      TRADE: path.career_track_distribution_trade,
      MID: path.career_track_distribution_mid,
      UPPER: path.career_track_distribution_upper,
      TOP: path.career_track_distribution_top,
    },
    askLubavButtonResponse: path.askLubavButtonResponse,
    media_url: path.media_url,
    caregiver_flexibility: {
      caregiver_flexibility_score: path.caregiver_flexibility_score,
      caregiver_flexibility_part_time_flexible_scheduling:
        path.caregiver_flexibility_part_time_flexible_scheduling,
      caregiver_flexibility_ease_of_reentry_after_gap:
        path.caregiver_flexibility_ease_of_reentry_after_gap,
      caregiver_flexibility_licensure_credential_stability:
        path.caregiver_flexibility_licensure_credential_stability,
      caregiver_flexibility_cultural_support_for_caregivers:
        path.caregiver_flexibility_cultural_support_for_caregivers,
      caregiver_flexibility_skill_half_life_change_velocity:
        path.caregiver_flexibility_skill_half_life_change_velocity,
      caregiver_flexibility_remote_location_flexibility:
        path.caregiver_flexibility_remote_location_flexibility,
      caregiver_flexibility_career_progression_flexibility:
        path.caregiver_flexibility_career_progression_flexibility,
      caregiver_flexibility_synthesis: path.caregiver_flexibility_synthesis,
    },
    ai_impact: {
      ai_impact_automation_risk: path.ai_impact_automation_risk,
      ai_impact_integration_intensity: path.ai_impact_integration_intensity,
      ai_impact_role_reinvention_need: path.ai_impact_role_reinvention_need,
      ai_impact_leverage_upside: path.ai_impact_leverage_upside,
      ai_impact_human_differentiator_index: path.ai_impact_human_differentiator_index,
      ai_impact_five_year_impact_summary: path.ai_impact_five_year_impact_summary,
    },
  }));
}
